import React, { useEffect, useRef, useState } from 'react';
import { Modal, ModalBody, ModalContent } from '@nextui-org/react';

// Custom hook for media query with optimized dependencies
const useMediaQuery = (query: string) => {
  const [matches, setMatches] = useState(() => {
    // Initialize with the current match state if window is available
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches;
    }
    return false;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const media = window.matchMedia(query);
    setMatches(media.matches);

    // Modern event listener approach
    const listener = (e: MediaQueryListEvent) => setMatches(e.matches);

    // Use modern addEventListener if available, fallback to addListener
    if (media.addEventListener) {
      media.addEventListener('change', listener);
      return () => media.removeEventListener('change', listener);
    } else {
      // Fallback for older browsers
      media.addListener(listener);
      return () => media.removeListener(listener);
    }
  }, [query]); // Remove matches from dependencies

  return matches;
};

interface ModalSlideFilterProps {
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
}

// Memoize motion variants to prevent recreating on each render
const motionVariants = {
  enter: {
    x: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
    },
  },
  exit: {
    x: '-100%',
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
};

// En el celular el panel ocupa toda la pantalla. Este archivo tiene que estar
// en el `content` de Tailwind de la admin; si no, estas clases no se generan
// y el ancho lo decide el contenido (se salía de la pantalla con texto grande).
const modalClassNames = {
  base: 'justify-start m-0 sm:m-0 p-0 h-dvh max-h-full w-full max-w-full',
  wrapper: 'items-start justify-start',
  body: 'p-0',
  closeButton: 'z-50',
} as const;

const ModalSlideFilter = React.memo(
  ({ children, isOpen, onClose }: ModalSlideFilterProps) => {
    const isMd = useMediaQuery('(min-width: 768px)');
    // En Android (Samsung y otros) el toque que abre el panel manda además un
    // "click" ~20 ms después; cae en el fondo del panel recién abierto y lo
    // cerraba al instante. Se ignora un cierre que llegue justo al abrir.
    const openedAt = useRef(0);
    useEffect(() => {
      if (isOpen) openedAt.current = performance.now();
    }, [isOpen]);

    const desktopStyle: React.CSSProperties = {
      width: isOpen ? 256 : 0,
      minWidth: isOpen ? 256 : 0,
      flexBasis: isOpen ? 256 : 0,
      opacity: isOpen ? 1 : 0,
      pointerEvents: isOpen ? 'auto' : 'none',
    };

    if (isMd) {
      return (
        <div
          className='h-screen bg-background shadow-md transition-all duration-300 z-30 relative shrink-0 overflow-hidden'
          style={desktopStyle}
        >
          <div className='h-full w-64'>{children}</div>
        </div>
      );
    }
    const handleContentClick = (e: React.MouseEvent) => {
      // Prevenir que el clic se propague al contenedor padre
      e.stopPropagation();
    };

    return (
      <Modal
        isOpen={isOpen}
        onOpenChange={(open) => {
          if (!open && performance.now() - openedAt.current > 400) {
            onClose();
          }
        }}
        classNames={modalClassNames}
        motionProps={{
          variants: motionVariants,
        }}
        radius='none'
        scrollBehavior='inside'
        isDismissable={false}
      >
        <ModalContent>
          <ModalBody onClick={handleContentClick}>{children}</ModalBody>
        </ModalContent>
      </Modal>
    );
  }
);

// Add display name for debugging
ModalSlideFilter.displayName = 'ModalSlideFilter';

export default ModalSlideFilter;
